import { getToken } from "firebase/messaging"

import store from "@/store"

import { isNativePush, isSupportedDevice } from "@/lib/is-platform"
import {
  setupPushStatusListener,
  postMessageToNativeApp,
  buildMessage,
  openSettings,
  MESSAGE_TYPES
} from "@/lib/app-store-utils"
import {
  PROVIDERS,
  NOTIFICATION_GRANTED,
  STATUSES
} from "@/store/notifications"
import messaging from "@/lib/fcm/messaging"
import { handleNotificationUrl } from "@/lib/handle-notification-url"

let isWebPushSetup = false

const isPushEnabled = () => store.getters["notifications/isPushEnabled"]()
const isSuspended = () => store.getters["notifications/isPushSuspended"]()

const updatePushState = async (canAsk, status, token, provider, deviceId) => {
  store.commit("notifications/setCanAsk", canAsk)
  store.commit("notifications/setStatus", status)
  store.commit("notifications/setToken", token)
  store.commit("notifications/setProvider", provider)
  store.commit("notifications/setPusherDeviceId", deviceId)

  await store.dispatch("notifications/updatePushEndpointFromState")
}

const setupWebPushNotifications = () => {
  if (isWebPushSetup || !("serviceWorker" in navigator)) {
    return
  }

  // Listen for messages from the service worker
  navigator.serviceWorker.addEventListener("message", event => {
    // console.log("[PN] Service worker message", event)

    if (
      event.data.type === "PUSH_NOTIFICATION_CLICK" &&
      event.data.payload?.click_action
    ) {
      handleNotificationUrl(event.data.payload.click_action)
    }
  })

  isWebPushSetup = true
}

const updateTokenAndMarkEnabled = async () => {
  const token = await getToken(messaging)

  await updatePushState(false, STATUSES.ENABLED, token, PROVIDERS.FCM)
}

export const initializePushNotifications = async () => {
  if (isNativePush) {
    setupPushStatusListener(status => {
      updatePushState(
        status.canAsk,
        status.granted ? STATUSES.ENABLED : STATUSES.DISABLED,
        status.token,
        PROVIDERS.EXPO
      )
    })
  } else if (isSupportedDevice()) {
    setupWebPushNotifications()

    await store.dispatch("notifications/fetchPushSettings")

    if (isPushEnabled() || isSuspended()) {
      updateTokenAndMarkEnabled()
    } else {
      updatePushState(true, STATUSES.DISABLED, null, PROVIDERS.FCM)
    }
  }

  await queryPushStatus()
}

export const queryPushStatus = () =>
  new Promise(resolve => {
    if (isNativePush) {
      postMessageToNativeApp(buildMessage(MESSAGE_TYPES.PUSH_STATUS_REQUEST))
      setTimeout(resolve, 1250)
    } else {
      if (store.getters["notifications/hasLoadedPushSettings"]) {
        resolve()
      } else {
        store.dispatch("notifications/fetchPushSettings").then(resolve)
      }
    }
  })

export const requestPermission = () =>
  new Promise((resolve, reject) => {
    if (isNativePush) {
      postMessageToNativeApp(
        buildMessage(MESSAGE_TYPES.PUSH_REQUEST_PERMISSION)
      )

      setTimeout(() => {
        if (isPushEnabled()) {
          resolve()
        } else {
          reject()
        }
      }, 3000)
    } else if (isSupportedDevice()) {
      Notification.requestPermission()
        .then(async permission => {
          if (permission === NOTIFICATION_GRANTED) {
            await updateTokenAndMarkEnabled()
            setupWebPushNotifications()
            resolve()
          } else {
            reject()
          }
        })
        .then(resolve)
        .catch(reject)
    } else {
      reject()
    }
  })

export const unsubscribeFromPushNotifications = async () => {
  if (isNativePush) {
    openSettings()
  } else {
    await updatePushState(false, STATUSES.DISABLED, null, PROVIDERS.FCM)
  }
}

export { openSettings, isNativePush, isSupportedDevice }
